/**
 * localStorage utilities for demo mode
 * Handles CRUD operations for templates and additional context presets in demo mode
 */

const TEMPLATES_STORAGE_KEY = 'demo_templates';
const ADDITIONAL_CONTEXT_STORAGE_KEY = 'demo_additional_context_presets';

// Default templates - must match the ones in store/reducers/index.js
const getDefaultTemplates = () => [
  {
    id: 'template-1',
    title: 'Template 1',
    color: '#2DD0D6',
    data: {
      regimen_solicitud: '10',
      declaracion_1: 'EX',
      declaracion_2: 'A',
      naturaleza_transaccion: '11',
       medio_transporte_frontera: '30',
      embalajes: [
        {
          marcas: [{ marca: 'TEST' }],
        },
      ],
    },
  },
  {
    id: 'template-2',
    title: 'Template 2',
    color: '#10B981',
    data: {
      aduana_despacho: '0441',
      pais_exportacion: 'ES',
      provincia: 'AL',
      localizacion_mercancia: '0441LAME05',
      documentos: [
        { codigo_documento: 'N380', tipo: 2 },
        { codigo_documento: 'N730', tipo: 4 },
        { codigo_documento: 'Y024', tipo: 3 },
      ],
    },
  },
  {
    id: 'template-3',
    title: 'Template 3',
    color: '#F59E0B',
    data: {
      aduana_despacho: '3161',
      pais_exportacion: 'ES',
      provincia: 'NA',
      localizacion_mercancia: '3161K30007',
      documentos: [
        { codigo_documento: 'N380', tipo: 2 },
        { codigo_documento: 'N730', tipo: 4 },
        { codigo_documento: 'N003', tipo: 2 },
      ],
    },
  },
];

// Templates localStorage operations
export const getTemplatesFromLocalStorage = () => {
  try {
    const stored = localStorage.getItem(TEMPLATES_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      // If we have stored templates, return them
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed;
      }
    }
    // If localStorage is empty or invalid, initialize with defaults
    const defaults = getDefaultTemplates();
    saveTemplatesToLocalStorage(defaults);
    return defaults;
  } catch (error) {
    console.error('Error reading templates from localStorage:', error);
    // On error, initialize with defaults
    const defaults = getDefaultTemplates();
    saveTemplatesToLocalStorage(defaults);
    return defaults;
  }
};

export const saveTemplatesToLocalStorage = (templates) => {
  try {
    localStorage.setItem(TEMPLATES_STORAGE_KEY, JSON.stringify(templates));
    return true;
  } catch (error) {
    console.error('Error saving templates to localStorage:', error);
    return false;
  }
};

export const addTemplateToLocalStorage = (template) => {
  try {
    const templates = getTemplatesFromLocalStorage();
    // Check if template with same ID already exists
    const templateExists = templates.some(t => t && t.id === template.id);
    if (templateExists) {
      // If exists, update it instead of adding duplicate
      return updateTemplateInLocalStorage(template);
    }
    // Add new template
    const updatedTemplates = [...templates, template];
    saveTemplatesToLocalStorage(updatedTemplates);
    return { success: true, template };
  } catch (error) {
    console.error('Error adding template to localStorage:', error);
    return { success: false, error: error.message };
  }
};

export const updateTemplateInLocalStorage = (template) => {
  try {
    const templates = getTemplatesFromLocalStorage();
    // Check if template exists in the array
    const templateExists = templates.some(t => t && t.id === template.id);
    
    let updatedTemplates;
    if (templateExists) {
      // Update existing template
      updatedTemplates = templates.map(t => 
        t && t.id === template.id ? template : t
      );
    } else {
      // Template doesn't exist, add it to the array
      updatedTemplates = [...templates, template];
    }
    
    saveTemplatesToLocalStorage(updatedTemplates);
    return { success: true, template };
  } catch (error) {
    console.error('Error updating template in localStorage:', error);
    return { success: false, error: error.message };
  }
};

export const deleteTemplateFromLocalStorage = (templateId) => {
  try {
    const templates = getTemplatesFromLocalStorage();
    const updatedTemplates = templates.filter(t => t && t.id !== templateId);
    saveTemplatesToLocalStorage(updatedTemplates);
    return { success: true };
  } catch (error) {
    console.error('Error deleting template from localStorage:', error);
    return { success: false, error: error.message };
  }
};

// Additional Context Presets localStorage operations
export const getAdditionalContextPresetsFromLocalStorage = () => {
  try {
    const stored = localStorage.getItem(ADDITIONAL_CONTEXT_STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.error('Error reading additional context presets from localStorage:', error);
    return [];
  }
};

export const saveAdditionalContextPresetsToLocalStorage = (presets) => {
  try {
    localStorage.setItem(ADDITIONAL_CONTEXT_STORAGE_KEY, JSON.stringify(presets));
    return true;
  } catch (error) {
    console.error('Error saving additional context presets to localStorage:', error);
    return false;
  }
};

export const addAdditionalContextPresetToLocalStorage = (preset) => {
  try {
    const presets = getAdditionalContextPresetsFromLocalStorage();
    // Generate ID if not provided
    const presetWithId = {
      ...preset,
      id: preset.id || `act-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    };
    const updatedPresets = [...presets, presetWithId];
    saveAdditionalContextPresetsToLocalStorage(updatedPresets);
    return { success: true, preset: presetWithId };
  } catch (error) {
    console.error('Error adding additional context preset to localStorage:', error);
    return { success: false, error: error.message };
  }
};

export const updateAdditionalContextPresetInLocalStorage = (preset) => {
  try {
    const presets = getAdditionalContextPresetsFromLocalStorage();
    const updatedPresets = presets.map(p => 
      p && p.id === preset.id ? preset : p
    );
    saveAdditionalContextPresetsToLocalStorage(updatedPresets);
    return { success: true, preset };
  } catch (error) {
    console.error('Error updating additional context preset in localStorage:', error);
    return { success: false, error: error.message };
  }
};

export const deleteAdditionalContextPresetFromLocalStorage = (presetId) => {
  try {
    const presets = getAdditionalContextPresetsFromLocalStorage();
    const updatedPresets = presets.filter(p => p && p.id !== presetId);
    saveAdditionalContextPresetsToLocalStorage(updatedPresets);
    return { success: true };
  } catch (error) {
    console.error('Error deleting additional context preset from localStorage:', error);
    return { success: false, error: error.message };
  }
};

